import { initAnalytics, track } from "./analytics";
import { pollPreviewStatus } from "./poll";
import type { Store as StoreType } from "./state";
import { Store } from "./state";
import styles from "./styles.css";
import { authenticateForUpload, getSupabase } from "./supabase";
import { getTurnstileToken } from "./turnstile";
import { render } from "./ui";
import { uploadVideo } from "./upload";
import {
  extractVideoMetadata,
  type VideoMetadata,
  validateFile,
} from "./validation";

async function mount(hostEl: HTMLElement) {
  if (hostEl.shadowRoot) return; // already mounted

  const shadow = hostEl.attachShadow({ mode: "open" });

  const styleEl = document.createElement("style");
  styleEl.textContent = styles as unknown as string;
  shadow.appendChild(styleEl);

  const container = document.createElement("div");
  container.className = "widget";
  shadow.appendChild(container);

  initAnalytics();
  track("anon_widget_mounted");

  const store = new Store();

  const update = () => {
    container.innerHTML = render(store.get());
    attachHandlers(container, store);
  };

  store.subscribe(update);
  update();

  // Attempt to resume an existing anonymous session + in-progress project.
  // Wrapped in try/catch so any failure (network, expired session, etc.)
  // just falls back to the initial dropzone state.
  try {
    const { getSupabase } = await import("./supabase");
    const supabase = getSupabase();
    const { data } = await supabase.auth.getSession();

    const isAnonymous = (data.session?.user as any)?.is_anonymous === true;
    if (!isAnonymous) return;

    const { trpcCall } = await import("./trpc");
    const existing = await trpcCall<{ projectId: string } | null>(
      "project.anonymousGetMostRecent",
      {},
      { method: "query" }
    ).catch(() => null);
    if (!existing) return;

    // Resume polling
    store.set({
      kind: "processing",
      projectId: existing.projectId,
      progress: 10,
    });
    const { pollPreviewStatus: resumePoll } = await import("./poll");
    const final = await resumePoll(existing.projectId, (status) => {
      if (status.status === "processing") {
        store.set({
          kind: "processing",
          projectId: existing.projectId,
          progress: status.progress,
        });
      }
    });

    if (final.status === "ready") {
      track("anon_processing_completed", { projectId: existing.projectId });
      store.set({
        kind: "previewReady",
        projectId: existing.projectId,
      });
      track("anon_preview_shown", { projectId: existing.projectId });
    } else if (final.status === "failed") {
      store.set({
        kind: "error",
        message: final.errorReason ?? "Processing failed",
        clearSessionOnRetry: true,
      });
    }
  } catch (err: any) {
    track("anon_error", {
      reasonCode: err?.code ?? "unknown",
      message: err?.message ?? "unknown",
    });
    console.warn("[widget] resume check failed", err);
  }
}

function attachHandlers(container: HTMLElement, store: StoreType) {
  const dropzone = container.querySelector(
    "[data-dropzone]"
  ) as HTMLElement | null;
  const fileInput = container.querySelector(
    "[data-file-input]"
  ) as HTMLInputElement | null;

  if (dropzone && fileInput) {
    dropzone.addEventListener("click", () => fileInput.click());

    dropzone.addEventListener("dragover", (e) => {
      e.preventDefault();
      dropzone.classList.add("drag-over");
    });

    dropzone.addEventListener("dragleave", () => {
      dropzone.classList.remove("drag-over");
    });

    dropzone.addEventListener("drop", (e) => {
      e.preventDefault();
      dropzone.classList.remove("drag-over");
      const file = e.dataTransfer?.files?.[0];
      if (file) handleFile(file, store);
    });

    fileInput.addEventListener("change", () => {
      const file = fileInput.files?.[0];
      if (file) handleFile(file, store);
    });
  }

  const retryBtn = container.querySelector("[data-retry]");
  if (retryBtn) {
    retryBtn.addEventListener("click", async () => {
      const current = store.get();
      // Only sign out when a backend project was actually created — otherwise the
      // failed project would be picked up by the resume flow (anonymousGetMostRecent)
      // on next mount. Client-side validation errors never created a project, so we
      // keep the session.
      if (current.kind === "error" && current.clearSessionOnRetry) {
        try {
          await getSupabase().auth.signOut();
        } catch (err) {
          console.warn("[widget] sign out on retry failed", err);
        }
      }
      store.set({ kind: "initial" });
    });
  }

  const uploadCta = container.querySelector("[data-upload-cta]");
  if (uploadCta) {
    uploadCta.addEventListener("click", () => {
      const current = store.get();
      if (current.kind !== "selected") return;
      startUpload(current.file, current.meta, current.previewUrl, store);
    });
  }

  const deleteCta = container.querySelector("[data-delete-cta]");
  if (deleteCta) {
    deleteCta.addEventListener("click", () => {
      const current = store.get();
      if (current.kind === "selected") URL.revokeObjectURL(current.previewUrl);
      store.set({ kind: "initial" });
    });
  }

  const editCta = container.querySelector(
    "[data-edit-cta]"
  ) as HTMLElement | null;
  if (editCta) {
    editCta.addEventListener("click", async () => {
      const projectId = editCta.dataset.projectId!;
      track("anon_cta_clicked", { projectId });
      const { getWidgetBaseUrl } = await import("./origin");
      const url = new URL(`${getWidgetBaseUrl()}/signup`);
      url.searchParams.set("projectId", projectId);
      url.searchParams.set("source", "widget");
      window.location.href = url.toString();
    });
  }
}

async function handleFile(file: File, store: StoreType) {
  store.set({ kind: "validating", file });

  try {
    const meta = await extractVideoMetadata(file);
    const validationError = validateFile(file, meta);
    if (validationError) {
      store.set({ kind: "error", message: validationError });
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    store.set({ kind: "selected", file, previewUrl, meta });
  } catch (err: any) {
    track("anon_error", {
      reasonCode: err?.code ?? "unknown",
      message: err?.message ?? "unknown",
    });
    store.set({
      kind: "error",
      message: err?.message ?? "Could not read video",
    });
  }
}

async function startUpload(
  file: File,
  meta: VideoMetadata,
  previewUrl: string,
  store: StoreType
) {
  URL.revokeObjectURL(previewUrl);

  try {
    track("anon_upload_started", {
      fileSize: file.size,
      duration: meta.duration,
    });

    store.set({ kind: "authenticating", file });
    const turnstileToken = await authenticateForUpload(getTurnstileToken);

    store.set({ kind: "creatingProject", file });

    const titleBase = file.name.replace(/\.[^.]+$/, "");
    const fileExtension = file.name.toLowerCase().endsWith(".mov")
      ? "mov"
      : "mp4";

    const projectId = await uploadVideo({
      file,
      title: titleBase,
      language: "en", // v1 default
      fileExtension,
      videoMetaData: meta,
      turnstileToken,
      onProgress: (progress, pid) => {
        store.set({ kind: "uploading", file, progress, projectId: pid });
      },
    });

    track("anon_upload_completed", { projectId });

    store.set({ kind: "processing", projectId, progress: 10 });

    const final = await pollPreviewStatus(projectId, (status) => {
      if (status.status === "processing") {
        store.set({
          kind: "processing",
          projectId,
          progress: status.progress,
        });
      }
    });

    if (final.status === "failed") {
      store.set({
        kind: "error",
        message: final.errorReason ?? "Processing failed",
        clearSessionOnRetry: true,
      });
      return;
    }

    if (final.status === "ready") {
      track("anon_processing_completed", { projectId });
      store.set({
        kind: "previewReady",
        projectId,
      });
      track("anon_preview_shown", { projectId });
    }
  } catch (err: any) {
    track("anon_error", {
      reasonCode: err?.code ?? "unknown",
      message: err?.message ?? "unknown",
    });
    store.set({
      kind: "error",
      message: err?.message ?? "Upload failed",
      clearSessionOnRetry: true,
    });
  }
}

function init() {
  const hosts = document.querySelectorAll<HTMLElement>(
    "#submagic-upload-widget"
  );
  hosts.forEach(mount);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
